import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Breadcrumb,
  Spinner,
  Alert,
  Form,
  Button,
  Tabs,
  Tab,
  Pagination,
} from "react-bootstrap";
import { useAuth } from "@clerk/clerk-react";
import createAxiosInstance from "../utility/utils";
import { Calendar3, PersonCircle } from "react-bootstrap-icons";

const BreadcrumbNav = () => (
  <Breadcrumb className="mb-4">
    {/* <Breadcrumb.Item href="/">Dashboard</Breadcrumb.Item> */}
    <Breadcrumb.Item active>My Attendance</Breadcrumb.Item>
  </Breadcrumb>
);

const StudentProfile = ({ profile, loading }) => {
  if (loading) {
    return (
      <Card className="mb-4 shadow">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" />
        </Card.Body>
      </Card>
    );
  }

  if (!profile) return null;

  return (
    <Card className="mb-4 shadow">
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <div className="me-3">
            <PersonCircle size={50} className="text-primary" />
          </div>
          <div>
            <h4 className="mb-1">{profile.name}</h4>
            <p className="text-muted mb-0">USN: {profile.usnNumber}</p>
          </div>
        </div>
        <hr />
        <Row>
          <Col md={6}>
            <p className="mb-1">
              <strong>Email:</strong> {profile.email}
            </p>
            <p className="mb-1">
              <strong>USN:</strong> {profile.usnNumber || "Not assigned"}
            </p>
          </Col>
          <Col md={6}>
            <p className="mb-1">
              <strong>Batch:</strong>{" "}
              {profile.batch?.map((b) => b.name).join(", ") || "Not assigned"}
            </p>
            <p className="mb-1">
              <strong>Status:</strong>{" "}
              <Badge bg={profile.active ? "success" : "danger"}>
                {profile.active ? "Active" : "Inactive"}
              </Badge>
            </p>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const AttendanceSummary = ({ summary, loading }) => {
  if (loading) {
    return (
      <Card className="mb-4 shadow">
        <Card.Header>
          <h5 className="mb-0">Attendance Summary</h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" />
        </Card.Body>
      </Card>
    );
  }

  if (!summary) return null;

  const { overallAttendance, subjects } = summary;

  return (
    <Card className="mb-4 shadow">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Attendance Summary</h5>
        <Badge
          bg={overallAttendance.percentage >= 75 ? "success" : "danger"}
          className="fs-6"
        >
          {overallAttendance.percentage}% Overall
        </Badge>
      </Card.Header>
      <Card.Body>
        <Row className="mb-4">
          <Col md={4} className="text-center border-end">
            <h6>Total Classes</h6>
            <h2>{overallAttendance.total}</h2>
          </Col>
          <Col md={4} className="text-center border-end">
            <h6>Present</h6>
            <h2>{overallAttendance.present}</h2>
          </Col>
          <Col md={4} className="text-center">
            <h6>Absent</h6>
            <h2>{overallAttendance.total - overallAttendance.present}</h2>
          </Col>
        </Row>

        <h5 className="mb-3">Subject-wise Attendance</h5>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Subject</th>
              <th>Present</th>
              <th>Absent</th>
              <th>Total</th>
              <th>Percentage</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {subjects.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">
                  No attendance records found
                </td>
              </tr>
            ) : (
              subjects.map((subject) => (
                <tr key={subject.subjectId}>
                  <td>{subject.subjectName}</td>
                  <td>{subject.stats.present}</td>
                  <td>{subject.stats.absent}</td>
                  <td>{subject.stats.total}</td>
                  <td>{subject.stats.percentage}%</td>
                  <td>
                    <Badge
                      bg={subject.stats.percentage >= 75 ? "success" : "danger"}
                    >
                      {subject.stats.percentage >= 75 ? "Good" : "Low"}
                    </Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

const AttendanceHistory = ({ subjects, loading, error }) => {
  const { getToken } = useAuth();
  const axiosInstance = useMemo(
    () => createAxiosInstance(getToken),
    [getToken]
  );

  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    subjectId: "",
    fromDate: "",
    toDate: "",
  });

  const fetchAttendanceHistory = async (page = 1) => {
    setHistoryLoading(true);
    setHistoryError("");

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
      });

      if (filters.subjectId) {
        params.append("subjectId", filters.subjectId);
      }

      if (filters.fromDate && filters.toDate) {
        params.append("fromDate", filters.fromDate);
        params.append("toDate", filters.toDate);
      }

      const response = await axiosInstance.get(
        `/api/students/attendance/history?${params}`
      );

      if (response.data.success) {
        setHistoryRecords(response.data.history.records);
        setTotalPages(response.data.history.pagination.pages);
        setCurrentPage(response.data.history.pagination.page);
      } else {
        throw new Error(response.data.error || "Failed to fetch history");
      }
    } catch (err) {
      setHistoryError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load attendance history"
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (subjects && subjects.length > 0) {
      fetchAttendanceHistory();
    }
  }, [filters, subjects]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAttendanceHistory(page);
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatTime = (timeString) => {
    const options = { hour: "2-digit", minute: "2-digit" };
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString(
      undefined,
      options
    );
  };

  if (loading) {
    return (
      <Card className="mb-4 shadow">
        <Card.Header>
          <h5 className="mb-0">Attendance History</h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-4 shadow">
      <Card.Header>
        <h5 className="mb-0">Attendance History</h5>
      </Card.Header>
      <Card.Body>
        <Form className="mb-4">
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Subject</Form.Label>
                <Form.Select
                  value={filters.subjectId}
                  onChange={(e) =>
                    handleFilterChange("subjectId", e.target.value)
                  }
                >
                  <option value="">All Subjects</option>
                  {subjects?.map((subject) => (
                    <option key={subject.subjectId} value={subject.subjectId}>
                      {subject.subjectName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) =>
                    handleFilterChange("fromDate", e.target.value)
                  }
                  max={filters.toDate || undefined}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange("toDate", e.target.value)}
                  min={filters.fromDate || undefined}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() =>
                  setFilters({
                    subjectId: "",
                    fromDate: "",
                    toDate: "",
                  })
                }
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Form>

        {historyError && (
          <Alert
            variant="danger"
            onClose={() => setHistoryError("")}
            dismissible
          >
            {historyError}
          </Alert>
        )}

        {historyLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Subject</th>
                  <th>Teacher</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {historyRecords.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No attendance records found
                    </td>
                  </tr>
                ) : (
                  historyRecords.map((record) => (
                    <tr key={record._id}>
                      <td>{formatDate(record.date)}</td>
                      <td>{formatTime(record.time)}</td>
                      <td>{record.subject?.name} </td>
                      <td>{record.teacher?.name}</td>
                      <td>
                        <Badge
                          bg={
                            record.attendanceStatus === "present"
                              ? "success"
                              : "danger"
                          }
                        >
                          {record.attendanceStatus === "present"
                            ? "Present"
                            : "Absent"}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <Pagination>
                  <Pagination.Prev
                    onClick={() =>
                      currentPage > 1 && handlePageChange(currentPage - 1)
                    }
                    disabled={currentPage === 1}
                  />
                  {currentPage > 2 && (
                    <Pagination.Item onClick={() => handlePageChange(1)}>
                      1
                    </Pagination.Item>
                  )}
                  {currentPage > 3 && <Pagination.Ellipsis />}
                  {Array.from({ length: 5 }, (_, i) => currentPage - 2 + i)
                    .filter((page) => page > 0 && page <= totalPages)
                    .map((page) => (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Pagination.Item>
                    ))}
                  {currentPage < totalPages - 2 && <Pagination.Ellipsis />}
                  {currentPage < totalPages - 1 && (
                    <Pagination.Item
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </Pagination.Item>
                  )}
                  <Pagination.Next
                    onClick={() =>
                      currentPage < totalPages &&
                      handlePageChange(currentPage + 1)
                    }
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

const AttendanceStatistics = ({ summary, loading }) => {
  if (loading || !summary) return null;

  // Calculate attendance trend
  const subjectsAbove75 = summary.subjects.filter(
    (subject) => subject.stats.percentage >= 75
  ).length;
  const subjectsBelow75 = summary.subjects.length - subjectsAbove75;

  return (
    <Card className="mb-4 shadow">
      <Card.Header>
        <h5 className="mb-0">Attendance Statistics</h5>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={6} className="mb-4">
            <h6 className="mb-3">Subject Performance</h6>
            <div className="position-relative">
              <div className="progress" style={{ height: "30px" }}>
                <div
                  className="progress-bar bg-success"
                  style={{
                    width: `${(subjectsAbove75 / summary.subjects.length) * 100}%`,
                  }}
                >
                  {subjectsAbove75} Subjects
                </div>
                <div
                  className="progress-bar bg-danger"
                  style={{
                    width: `${(subjectsBelow75 / summary.subjects.length) * 100}%`,
                  }}
                >
                  {subjectsBelow75} Subjects
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-between mt-2">
              <div>
                <Badge bg="success" className="me-2"></Badge>
                Above 75%
              </div>
              <div>
                <Badge bg="danger" className="me-2"></Badge>
                Below 75%
              </div>
            </div>
          </Col>
          {/* <Col md={6}>
            <h6 className="mb-3">Required Classes to Reach 75%</h6>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Required Classes</th>
                </tr>
              </thead>
              <tbody>
                {summary.subjects
                  .filter((subject) => subject.stats.percentage < 75)
                  .map((subject) => {
                    // Calculate required classes to reach 75%
                    const currentAttendance = subject.stats.present;
                    const totalClasses = subject.stats.total;
                    const requiredAttendance = Math.ceil(
                      (totalClasses * 0.75 - currentAttendance) / 0.75
                    );

                    return (
                      <tr key={subject.subjectId}>
                        <td>{subject.subjectName}</td>
                        <td>
                          {requiredAttendance <= 0 ? (
                            <Badge bg="success">On Track</Badge>
                          ) : (
                            <Badge bg="warning" text="dark">
                              Need {requiredAttendance} more
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                {summary.subjects.filter(
                  (subject) => subject.stats.percentage < 75
                ).length === 0 && (
                  <tr>
                    <td colSpan="2" className="text-center">
                      All subjects have above 75% attendance
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col> */}
        </Row>
      </Card.Body>
    </Card>
  );
};

const StudentDashboard = () => {
  const { getToken } = useAuth();
  const axiosInstance = useMemo(
    () => createAxiosInstance(getToken),
    [getToken]
  );

  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("summary");

  const fetchStudentProfile = async () => {
    try {
      const response = await axiosInstance.get("/api/students/profile");
      if (response.data.success) {
        setProfile(response.data.student);
      } else {
        throw new Error(response.data.error || "Failed to fetch profile");
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load student profile"
      );
    }
  };

  const fetchAttendanceSummary = async () => {
    try {
      const response = await axiosInstance.get(
        "/api/students/attendance/summary"
      );
      if (response.data.success) {
        setSummary(response.data.summary);
      } else {
        throw new Error(response.data.error || "Failed to fetch summary");
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load attendance summary"
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        await Promise.all([fetchStudentProfile(), fetchAttendanceSummary()]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Container fluid className="py-4">
      <BreadcrumbNav />

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      <StudentProfile profile={profile} loading={loading} />

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab
          eventKey="summary"
          title={
            <div className="d-flex align-items-center">
              <Calendar3 className="me-1" />
              Summary
            </div>
          }
        >
          <AttendanceSummary summary={summary} loading={loading} />
          <AttendanceStatistics summary={summary} loading={loading} />
        </Tab>
        <Tab
          eventKey="history"
          title={
            <div className="d-flex align-items-center">
              <Calendar3 className="me-1" />
              History
            </div>
          }
        >
          <AttendanceHistory
            subjects={summary?.subjects}
            loading={loading}
            error={error}
          />
        </Tab>
      </Tabs>
    </Container>
  );
};

export default StudentDashboard;
